<template>
  <div id="productVariants" class="product-description">
    <h1 class="flex text-xl text-pdsPrimaryColor">
      <span v-if="baseProductDetail.manufName">{{ $toTitleCase(title) }} - {{ baseProductDetail.manufName }}</span>
      <span v-else>{{ $toTitleCase(title) }}</span>
    </h1>
    <div
      class="grid grid-rows md:grid-cols-1 gap-4"
      v-if="baseProductDetail.products && baseProductDetail.products.length > 0"
    >
      <card
        v-for="item in baseProductDetail.products"
        :key="item.productId"
        class="border border-disabled-background h-full flex flex-col relative"
      >
        <template v-slot>
          <div class="absolute top-2 right-2" @click.prevent="favorite(item, baseProductDetail)">
            <component
              v-if="!isReplenishmentFlow"
              :is="item.isFavourite ? 'HeartSolidIcon' : 'HeartIcon'"
              class="h-5 w-5 text-primary-default cursor-pointer"
            />
          </div>
          <div class="flex flex-col md:flex-row flex-grow gap-2">
            <div class="flex-col">
              <div class="md:text-base text-xs text-tdefault-color font-bold mb-2 cursor-pointer overflow-hidden">
                {{ $toTitleCase(item.title) }}
              </div>
              <div class="flex-row mb-2">
                <span class="text-tdefault-color md:text-base text-xs mr-2">{{ item.productCode }}</span>
                <span class="text-tdefault-color md:text-base text-xs pl-2 border-l border-stone-200">
                  {{ item.packagingUnit }} {{ item.packagingLabel }}
                </span>
              </div>
              <div class="">
                <div class="mb-2">
                  <span
                    v-if="isCustomerFlow"
                    class="inline-flex gap-1 border-b border-dashed md:text-base text-xs"
                    :class="
                      item.isInsured
                        ? 'text-success-default border-success-default'
                        : 'text-danger-text border-danger-text'
                    "
                  >
                    <component
                      :is="item.isInsured ? 'CheckCircleIcon' : 'ExclamationCircleIcon'"
                      class="h-4 w-4 md:h-5 md:w-5"
                    />
                    {{ item.isInsured ? 'Vergoed' : 'Niet vergoed' }}
                  </span>
                </div>
                <div class="md:ml-5 mb-2">
                  <!-- <span
                    class="inline-flex gap-1 border-b border-dashed md:text-base text-xs"
                    :class="
                      item.orderable
                        ? 'text-success-default border-success-default'
                        : 'text-warning-default border-warning-default'
                    "
                  >
                    <component
                      :is="item.orderable ? 'CheckCircleIcon' : 'XCircleIcon'"
                      class="h-4 w-4 md:h-5 md:w-5"
                    />
                    {{ item.orderable ? 'Leverbaar' : 'Niet leverbaar' }}
                  </span> -->
                </div>
              </div>
            </div>
            <div class="grid mx-auto mt-4 md:mt-0 content-center relative md:absolute md:right-2 self-center">
              <button
                v-if="
                  isProductDetailsPageFlow(item.isAlternativesBPIdAvailable) && !item.orderable && !isAssortmentFlow
                "
                class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-white bg-primary-default bg-cta rounded-full items-center mb-2 mr-2"
                @click.prevent="goToAlternativesProductDetailsPage(item.alternativesBaseProductId)"
              >
                {{ getButtonText(item.productRelationType) }}
              </button>
              <button
                v-else-if="item.orderable && !isAssortmentFlow"
                class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-xs md:text-base font-medium text-white bg-cta rounded-full items-center mb-2"
                @click.prevent="order(item.productId, 1)"
              >
                Bestellen
              </button>
              <div class="mb-2" v-else-if="String(item.MaterialStatusCode) === '30' && !isAssortmentFlow">
                Op bestelling verkrijgbaar
              </div>
              <div class="mb-2" v-else-if="!item.orderable && !isAssortmentFlow">Niet bestelbaar</div>
            </div>
          </div>
        </template>
      </card>
    </div>
    <v-warning-message
      v-if="baseProductDetail.products && baseProductDetail.products.length === 0"
      :class="{ 'zoomOut animated': animated }"
      :warning="'Het is nog niet mogelijk om dit product te bestellen. Gelieve een ander product uit de catalogus te kiezen.'"
      :html="true"
      :is-urgent="false"
    />
  </div>
  <modal-dialog :modal-open-status="isPrompt" :showCloseButton="true" @close="closeOverlay">
    <template #header>
      <div class="text-primary-default">
        <h2 class="text-lg font-bold my-2">Product valt niet binnen de machtiging(en).</h2>
      </div>
    </template>
    <template #body>
      <div class="grid grid-rows gap-4">
        <div>
          Zonder machtiging ontvangt de patiënt geen vergoeding van de zorgverzekeraar. Weet u zeker dat u wilt
          doorgaan?
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex flex-row gap-2">
        <button
          @click.prevent="continueAddProduct"
          class="flex justify-center py-2 px-2 md:px-4 text-xs md:text-base font-medium text-pdsPrimaryColor items-center bg-white rounded-full focus:outline-none"
        >
          <span class="md:ml-1">Toch bestellen</span>
        </button>
        <button
          @click.prevent="$router.push('/patientdashboard')"
          class="flex justify-center py-2 px-2 md:px-4 text-xs md:text-base font-medium text-white items-center bg-primary-default border border-primary-default rounded-full ml-auto text-nowrap"
        >
          <span class="md:ml-1"> Machtiging aanmaken </span>
        </button>
      </div>
    </template>
  </modal-dialog>
  <modal-dialog :modal-open-status="showProductFavourites">
    <template #body>
      <v-product-favourites
        :base-product="selectedBaseProductForFavorite"
        :product="selectedProductForFavorite"
        :product-id="favProductId"
        @success="successSaveFavorite(...arguments)"
        @cancel="closeFavOverlay"
      />
    </template>
  </modal-dialog>
</template>

<script>
import VWarningMessage from '@/components/shared/WarningMessage';
import { HeartIcon as HeartSolidIcon } from '@heroicons/vue/20/solid';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon, HeartIcon } from '@heroicons/vue/24/outline';
import VHeartOutlineIcon from 'icons/HeartOutline';
import router from '@/router';
import { addToShoppingCart, shoppingCart$, activePpwRecId$ } from '@/services';
import { ref, computed, getCurrentInstance } from 'vue';
import VProductFavourites from './ProductFavourites';

export default {
  name: 'ProductDescription',
  components: {
    HeartIcon,
    VHeartOutlineIcon,
    VWarningMessage,
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon,
    HeartSolidIcon,
    VProductFavourites
  },
  props: {
    title: String,
    productId: String,
    sapId: String,
    shortDescription: String,
    productUnit: String,
    baseProductDetail: Object
  },
  setup(props) {
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    const shoppingCart = ref(null);
    const activePpwRecId = ref(null);
    const isPrompt = ref(false);
    const currentOrderDetails = ref({});
    const favoriteStatusEnum = {
      false: 'v-heart-outline-icon',
      true: 'v-heart-icon'
    };
    const showProductFavourites = ref(false);
    const selectedBaseProductForFavorite = ref(null);
    const selectedProductForFavorite = ref(null);
    const favProductId = ref(null);

    const isReplenishmentFlow = ref(router.currentRoute.value.meta.flow === 'replenishment');
    const isAssortmentFlow = ref(router.currentRoute.value.meta.flow === 'assortment');
    const isCustomerFlow = ref(router.currentRoute.value.meta.flow === 'customer');
    const isProductDetailsPageFlow = (isAlternativesBPIdAvailable) => {
      return isAlternativesBPIdAvailable;
    };

    shoppingCart$.subscribe((value) => {
      shoppingCart.value = value;
    });

    activePpwRecId$.subscribe((value) => {
      activePpwRecId.value = value;
    });

    const showAlert = (productId) => {
      const orderedProduct = props.baseProductDetail.products.find((p) => p.productId === productId);

      if (orderedProduct) {
        $emitter.emit('notification', {
          title: `${orderedProduct.title} is toegevoegd.`,
          htmlMessage: `<p class="underline cursor-pointer text-gray-900" data-route="/bestellijst">Bestelling afronden</p>`
        });
      }
    };

    const order = (productId, count) => {
      let order = props.baseProductDetail;
      if (order) {
        let isInsuredProd =
          props.baseProductDetail.products.filter((x) => x.productId === productId && x.isInsured === true).length > 0;
        let isConsignmentProduct =
          props.baseProductDetail.products.filter((x) => x.productId === productId && x.isConsignmentProduct === true)
            .length > 0;
        if (!isInsuredProd && !isReplenishmentFlow.value) {
          isPrompt.value = true;
          currentOrderDetails.value = {
            productId: productId,
            amount: count,
            isConsignmentProduct: isConsignmentProduct
          };
        } else {
          isPrompt.value = false;
          order.isInsured = isInsuredProd;
          order.addedToBasket = true;
          order.count = count;
          addToShoppingCart(shoppingCart.value, productId, count, activePpwRecId.value, isConsignmentProduct);
          showAlert(productId);
        }
      }
    };

    const continueAddProduct = () => {
      isPrompt.value = false;
      addToShoppingCart(
        shoppingCart.value,
        currentOrderDetails.value.productId,
        currentOrderDetails.value.amount,
        activePpwRecId.value,
        currentOrderDetails.value.isConsignmentProduct
      );
      showAlert(currentOrderDetails.value.productId);
      currentOrderDetails.value = {};
    };

    const closeOverlay = () => {
      isPrompt.value = false;
    };

    const computeBaseProductDetails = () => {
      $emitter.emit('favouriteSuccess');
    };

    const getEnumValue = (enumList, enumKey) => {
      if (enumKey === 'null' || enumKey === '') {
        return enumList.false;
      }
      for (const key in enumList) {
        if (enumList.hasOwnProperty(key)) {
          if (key === enumKey) {
            return enumList[key];
          }
        }
      }
    };

    const goToAlternativesProductDetailsPage = (baseProductId) => {
      if (baseProductId) {
        router.push('/catalog/baseProduct/' + baseProductId);
      }
    };

    const favorite = (product, baseProduct) => {
      const productId = product.productId;

      selectedProductForFavorite.value = product;
      selectedBaseProductForFavorite.value = baseProduct;
      favProductId.value = productId;
      showProductFavourites.value = true;
    };

    const closeFavOverlay = () => {
      showProductFavourites.value = false;
    };

    const successSaveFavorite = (isAdded) => {
      selectedProductForFavorite.value = null;
      selectedBaseProductForFavorite.value = null;
      favProductId.value = null;
      closeFavOverlay();
      $emitter.emit('favouriteSuccess');
    };

    const getButtonText = (productRelationType) => {
      switch (productRelationType) {
        case 'Followup':
          return 'Bekijk opvolgend product';
        case 'Substitute':
          return 'Bekijk vervangend product';
        case 'Alternatives':
          return 'Bekijk alternatieve product';
        default:
      }
    };

    return {
      shoppingCart,
      activePpwRecId,
      isPrompt,
      currentOrderDetails,
      favoriteStatusEnum,
      showProductFavourites,
      selectedBaseProductForFavorite,
      favProductId,
      selectedProductForFavorite,
      isReplenishmentFlow,
      isAssortmentFlow,
      isCustomerFlow,
      isProductDetailsPageFlow,
      showAlert,
      order,
      continueAddProduct,
      closeOverlay,
      computeBaseProductDetails,
      getEnumValue,
      goToAlternativesProductDetailsPage,
      favorite,
      closeFavOverlay,
      successSaveFavorite,
      getButtonText
    };
  }
};
</script>
